import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Leaf, Users } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ayurmed-india/artifacts/v8gcebn1_8E13F1F6-7E3F-45F7-9F22-32F122F8D9CF.jpeg";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Our Story', path: '/about' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const products = [
    { name: 'Adloda-EX', path: '/products/adloda-ex' },
    { name: 'Adloda-DS', path: '/products/adloda-ds' },
    { name: 'Rasna Gulgulu', path: '/products/rasna-gulgulu' },
    { name: 'Thara Gith', path: '/products/thara-gith' },
    { name: 'Rithu', path: '/products/rithu' },
  ];

  return (
    <footer data-testid="footer" className="bg-[#1C1C1C] text-white">
      {/* Heritage Section with Image */}
      <div className="bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-6">
              <h3 className="font-heading text-3xl md:text-4xl font-bold text-[#1C1C1C]">
                From Kerala, with<br />Ayurvedic Heritage
              </h3>
              <p className="text-[#57534E] leading-relaxed">
                Rooted in the renowned Peringattuthodiyil Ayurvedic family, Amjatha Pharma 
                carries forward generations of healing wisdom, thoughtfully adapted for today's world.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#E7E5E4]">
                  <Leaf className="w-4 h-4 text-[#2D4A3E]" />
                  <span className="text-sm font-medium text-[#2D4A3E]">Traditional Formulations</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#E7E5E4]">
                  <Users className="w-4 h-4 text-[#2D4A3E]" />
                  <span className="text-sm font-medium text-[#2D4A3E]">Trusted for Generations</span>
                </div>
              </div>
              <p className="text-sm font-medium text-[#C69C6D] italic">
                Proudly made in Kerala, India
              </p>
            </div>
            
            {/* Ayurvedic Image - User provided */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1624629747980-9028b99db266?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxoZXJiYWwlMjBvaWwlMjBtYXNzYWdlJTIwYXl1cnZlZGljJTIwdHJlYXRtZW50JTIwc3BhJTIwbmF0dXJhbCUyMGluZ3JlZGllbnRzfGVufDB8fHx8MTc3MDMyOTAxNHww&ixlib=rb-4.1.0&q=85"
                  alt="Traditional Ayurvedic spa ingredients"
                  className="w-full h-64 md:h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand Section */}
          <div className="md:col-span-5">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="Amjatha Pharma" 
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-[#A8A29E] leading-relaxed mb-6 max-w-md">
              Bringing the ancient wisdom of Ayurveda to modern healthcare. 
              Our products are crafted with authentic herbs and time-tested formulations 
              for your holistic well-being.
            </p>
            <div className="flex items-center gap-2 text-[#C69C6D]">
              <span className="font-accent italic text-lg">"Healing through Nature"</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h4 className="font-heading text-xl font-semibold mb-6 text-[#C69C6D]">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-[#A8A29E] hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="md:col-span-2">
            <h4 className="font-heading text-xl font-semibold mb-6 text-[#C69C6D]">
              Our Products
            </h4>
            <ul className="space-y-3">
              {products.map((product) => (
                <li key={product.name}>
                  <Link
                    to={product.path}
                    data-testid={`footer-product-${product.name.toLowerCase().replace(' ', '-')}`}
                    className="text-[#A8A29E] hover:text-white transition-colors"
                  >
                    {product.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="md:col-span-3">
            <h4 className="font-heading text-xl font-semibold mb-6 text-[#C69C6D]">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C69C6D] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-[#A8A29E]">
                  Amjatha Pharma<br />
                  Irimbiliyam, Valanchery<br />
                  Kerala, India - 679572
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#C69C6D] flex-shrink-0" strokeWidth={1.5} />
                <a href="tel:+919847023246" className="text-[#A8A29E] hover:text-white transition-colors">
                  +91 98470 23246
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#C69C6D] flex-shrink-0" strokeWidth={1.5} />
                <a href="mailto:amjathapharma@gmail.com" className="text-[#A8A29E] hover:text-white transition-colors">
                  amjathapharma@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#333]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#A8A29E] text-sm">
              &copy; {currentYear} Amjatha Pharma. All rights reserved.
            </p>
            <p className="text-[#A8A29E] text-sm">
              GMP Certified | ISO 9001:2015 | AYUSH Approved
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
